import {useFormik} from 'formik'
import EventPage from 'organization/Event/EventPage'
import {EventFaqHelp} from 'organization/Event/Help/FAQ'
import {useOrganizationEvent} from 'organization/Event/OrganizationEventProvider'
import {EventDates} from 'organization/Event/Settings/EventDates'
import {EventEnded} from 'organization/Event/Settings/EventEnded'
import {EventExtend} from 'organization/Event/Settings/EventExtend'
import {PublishSection} from 'organization/Event/Settings/PublishSection'
import {SaveEventData} from 'organization/Event/Settings/SaveEvent'
import BreadcrumMenu from 'organization/theme/Breadcrum'
import React from 'react'
import {confirmAlert} from 'react-confirm-alert'
import {toast} from 'react-toastify'
import styled from 'styled-components'
import {useI18n} from 'i18n/I18nContext'
import {
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  appLocaleToBcp47,
} from 'i18n/locales'

export const Settings = () => {
  const {event, updateEvent, makeEventPublic, loading} = useOrganizationEvent()
  const {t, locale} = useI18n()

  const isSlugDisabled = event?.is_ended

  const {handleSubmit, handleBlur, handleChange, values} = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: event?.name ?? '',
      slug: event?.slug ?? '',
      time: event?.time ?? '',
      page_date: event?.page_date ?? undefined,
      locale: (event as {locale?: string})?.locale ?? DEFAULT_LOCALE,
    },
    onSubmit: async (values: SaveEventData) => {
      const result = await updateEvent(values)

      if (result === true) {
        toast.success(t('settings.saved'))
      }
    },
  })

  const handlePublicClick = async () => {
    confirmAlert({
      message: t('settings.publishConfirm'),
      buttons: [
        {
          label: t('common.yes'),
          onClick: async () => {
            const loginResult = await makeEventPublic()

            if (loginResult === true) {
              toast.success(t('settings.publishedToast'))
            }
          },
        },
        {
          label: t('common.no'),
          onClick: () => {
            // nothing
          },
        },
      ],
    })
  }

  // const handleEndedClick = async () => {
  //   confirmAlert({
  //     message: 'Сигурни ли сте, че искате да приключите събитието?',
  //     buttons: [
  //       {
  //         label: 'Да',
  //         onClick: async () => {
  //           const loginResult = await endEvent()

  //           if (loginResult === true) {
  //             toast.success('Събитието е спряно')
  //           }
  //         },
  //       },
  //       {
  //         label: 'Не',
  //         onClick: () => {
  //           // nothing
  //         },
  //       },
  //     ],
  //   })
  // }

  return (
    <EventPage>
      <div className="row pl-30 pb-30 pr-30">
        <BreadcrumMenu
          title={t('settings.breadcrumb')}
          helpInfo={<EventFaqHelp faqKey="settingsPage" />}
        />

        {!event?.date && !event?.is_ended && <EventDates event={event} />}
        {event?.date && !event?.is_ended && <EventExtend event={event} />}
        {event?.date && event?.is_ended && <EventEnded />}

        <div className="col-xxl-12">
          <div className="create__event-area">
            <div className="body__card-wrapper">
              {/* create event form  */}
              <form onSubmit={handleSubmit}>
                <div className="create__event-main pt-25">
                  <div className="event__left-box">
                    <div className="create__input-wrapper">
                      <div className="singel__input-field mb-15">
                        <label className="input__field-text">
                          {t('settings.eventName')}
                        </label>

                        <input
                          type="text"
                          name="name"
                          placeholder={t('settings.eventNamePh')}
                          defaultValue={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                        />
                      </div>

                      <div className="singel__input-field mb-15">
                        <label className="input__field-text">
                          {t('settings.eventUrl')}{' '}
                          <AddressShow>{`www.eventa.tv/${values.slug}`}</AddressShow>
                        </label>
                        <InputGroup>
                          <span>www.eventa.tv/</span>
                          <input
                            type="text"
                            name="slug"
                            placeholder={t('settings.urlPh')}
                            value={values.slug}
                            onChange={(e) => {
                              handleAddressChange(e, handleChange)
                            }}
                            onBlur={handleBlur}
                            required
                            disabled={isSlugDisabled}
                          />
                        </InputGroup>
                      </div>

                      <div className="singel__input-field mb-15">
                        <label className="input__field-text">
                          {t('event.localeLabel')}
                        </label>
                        <small
                          className="d-block mb-10"
                          style={{display: 'block'}}
                        >
                          <i>{t('event.localeHint')}</i>
                        </small>
                        <select
                          name="locale"
                          className="form-select"
                          value={values.locale as string}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          {SUPPORTED_LOCALES.map((loc) => (
                            <option key={loc} value={loc}>
                              {LOCALE_LABELS[loc]}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="singel__input-field mb-15">
                        <label className="input__field-text">
                          {t('settings.eventTime')}
                        </label>

                        <input
                          type="time"
                          name="time"
                          placeholder={t('settings.timePh')}
                          defaultValue={values.time}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          readOnly={event?.is_public}
                          required
                        />
                      </div>

                      <div className="row g-20 ">
                        <div className="singel__input-field mb-15">
                          <label className="input__field-text">
                            {t('settings.privatePageAfter')}
                          </label>
                          <input
                            name="page_date"
                            type="datetime-local"
                            onChange={handleChange}
                            lang={appLocaleToBcp47(locale)}
                            value={values.page_date?.toString()}
                          />
                        </div>
                        <small>
                          <i>
                            {t('settings.pageDateHelp1')}
                            <br />
                            {t('settings.pageDateHelp2')}
                          </i>
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="event__right-box">
                    <div className="row g-20">
                      <div className="pricing__item mb-30">
                        <div className="pricing__item-header">
                          <div>
                            <span className="pricing__item-title">
                              {t('settings.publicAccess')}
                            </span>
                          </div>
                        </div>

                        <div className="pricing__item-content">
                          <ul>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('settings.publicBullet1')}
                            </li>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('settings.publicBullet2')}
                            </li>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('settings.publicBullet3')}
                            </li>
                            <li>
                              <i className="flaticon-warning-1"></i>{' '}
                              {t('settings.publicBullet4')}
                            </li>
                          </ul>
                        </div>
                        <div className="pricing__btn-wrap">
                          <PublishSection
                            event={event}
                            handlePublicClick={handlePublicClick}
                            loading={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={
                    loading
                      ? 'disableBtnStyle w-100 mt-30'
                      : 'input__btn w-100 mt-30'
                  }
                >
                  {t('common.save')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </EventPage>
  )
}

export const handleAddressChange = (e: any, callback: any) => {
  const re = /^[a-zA-Z0-9-]+$/
  const value = e.target.value
  const replacedSpace = value.replace(/ /g, '-')
  if (value === '' || re.test(replacedSpace)) {
    e.target.value = replacedSpace
    callback(e)
  }
}

const InputGroup = styled.div`
  display: flex;

  & > span {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px 15px;
    border: 1px solid var(--clr-border-4);
    border-radius: 4px;
    border-right: none;
  }
`
const AddressShow = styled.span`
  color: blue;
  cursor: pointer;
`
